import { filter, forEach, isNil } from 'lodash';
import { call, fork, put, select, takeEvery } from 'redux-saga/effects';
import { BRAND_EXPOSURE_LIST_STATUS, BRAND_TIER_LIST_STATUS } from '../../../constants/service/app/brandConstants';
import { CONFIRM_TAG_LIST_TYPE } from '../../../constants/service/app/tagConstants';
import { BRAND_ALL_LIST_URL_PATH, BRAND_JOIN_LIST_URL_PATH } from '../../../constants/service/common/urlConstants';
import brandToastMsg from '../../../constants/service/toastMsg/brandToastMsg';
import BrandApi from '../../../lib/api/brand/BrandApi';
import {
  asyncSimpleModal,
  excelDownLoad,
  reDirectPage,
  setRequestForm,
  setResponseListForm,
} from '../../../lib/common';
import toastMsg from '../../../lib/toastMsg';
import noticeExcelDownloadResult from '../../../lib/noticeExcelDownloadResult';
import { appActions } from '../app';
import brandActions from './actions';
import { BRAND_EXPOSURE_SELECTOR_FILTER_OPTION_MODEL, BRAND_TIER_SELECTOR_FILTER_OPTION_MODEL } from './models';
import { BALANCE_PERIOD_TYPE } from '../../../constants/service/app/balanceConstants';

function* fetchList() {
  /**
   *  [브랜드] 입점승인 브랜드 리스트 요청하기
   */
  yield takeEvery(brandActions.GET_BRAND_JOIN_LIST_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(BrandApi.getBrandJoinList, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(brandToastMsg.GET_BRAND_JOIN_LIST_REQUEST_SUCCESS);

        const data = {
          brandJoinList: setResponseListForm(result.data.brandJoinList, result.data.totalCnt, payload),
        };

        yield put({ type: brandActions.GET_BRAND_JOIN_LIST_SUCCESS, payload: data });

        /**
         *  현재 어떤 목록 페이지 인지 기록
         */
        yield put(
          brandActions.setData({
            path: ['listPageUrl'],
            value: BRAND_JOIN_LIST_URL_PATH,
          }),
        );
      }
    } catch (error) {
      toastMsg.error(brandToastMsg.GET_BRAND_JOIN_LIST_REQUEST_ERROR, error);
    }
  });

  /**
   *  [브랜드] 입점승인 브랜드 excel 리스트 다운로드 하기
   */
  yield takeEvery(brandActions.GET_BRAND_JOIN_EXCEL_DOWNLOAD_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(BrandApi.getBrandJoinExcelDownload, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(brandToastMsg.GET_BRAND_JOIN_LIST_EXCEL_DOWNLOAD_REQUEST_SUCCESS);

        excelDownLoad(payload.brandJoinPageName, result.data.brandJoinListExcelSheet);
      }
    } catch (error) {
      toastMsg.error(error.message || brandToastMsg.GET_BRAND_JOIN_LIST_EXCEL_DOWNLOAD_REQUEST_ERROR);
    }
  });

  /**
   *  [브랜드] 브랜드 리스트 페이지 전체 데이터 요청하기
   */
  yield takeEvery(brandActions.GET_BRAND_ALL_INFO_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(BrandApi.getBrandAllInfo, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(brandToastMsg.GET_BRAND_ALL_INFO_REQUEST_SUCCESS);

        const data = {
          brandSummaryBox: result.data.brandSummaryBox,
          currentTierList: result.data.currentTierList,
          brandList: setResponseListForm(result.data.brandList, result.data.totalCnt, payload),
        };

        yield put({ type: brandActions.GET_BRAND_ALL_INFO_SUCCESS, payload: data });

        /**
         *  현재 어떤 목록 페이지 인지 기록
         */
        yield put(
          brandActions.setData({
            path: ['listPageUrl'],
            value: BRAND_ALL_LIST_URL_PATH,
          }),
        );
      }
    } catch (error) {
      toastMsg.error(brandToastMsg.GET_BRAND_ALL_INFO_REQUEST_ERROR);
    }
  });

  /**
   *  [브랜드] 브랜드 리스트 요청하기
   */
  yield takeEvery(brandActions.GET_BRAND_LIST_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(BrandApi.getBrandList, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(brandToastMsg.GET_BRAND_LIST_REQUEST_SUCCESS);

        const data = {
          brandList: setResponseListForm(result.data.brandList, result.data.totalCnt, payload),
        };

        yield put({ type: brandActions.GET_BRAND_LIST_SUCCESS, payload: data });
      }
    } catch (error) {
      toastMsg.error(brandToastMsg.GET_BRAND_LIST_REQUEST_ERROR);
    }
  });

  /**
   *  [브랜드] 브랜드 excel 리스트 다운로드 하기
   */
  yield takeEvery(brandActions.GET_BRAND_EXCEL_DOWNLOAD_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(BrandApi.getBrandExcelDownload, setRequestForm(payload, true));
      noticeExcelDownloadResult(payload.email, result.msg);
    } catch (error) {
      toastMsg.error(error.message || brandToastMsg.GET_BRAND_LIST_EXCEL_DOWNLOAD_REQUEST_ERROR);
    }
  });

  /**
   * [브랜드] 브랜드 태그 검수 리스트 요청하기
   */
  yield takeEvery(brandActions.GET_BRAND_TAG_REQUEST_LIST_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(BrandApi.getBrandTagRequestList, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
        return;
      }

      toastMsg.success(brandToastMsg.GET_BRAND_TAG_REQUEST_LIST_REQUEST_SUCCESS);

      const data = {
        brandList: setResponseListForm(result.data.brandList, result.data.totalCnt, payload),
      };

      yield put({ type: brandActions.GET_BRAND_LIST_SUCCESS, payload: data });
    } catch (error) {
      toastMsg.error(brandToastMsg.GET_BRAND_TAG_REQUEST_LIST_REQUEST_ERROR, error);
    }
  });

  /**
   * [브랜드] 브랜드 태그 검수 excel 리스트 다운로드 하기
   */
  yield takeEvery(brandActions.GET_BRAND_TAG_REQUEST_EXCEL_DOWNLOAD_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(BrandApi.getBrandTagRequestDownLoad, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
        return;
      }

      toastMsg.success(brandToastMsg.GET_BRAND_TAG_REQUEST_EXCEL_DOWNLOAD_REQUEST_SUCCESS);

      excelDownLoad(payload.pageName, result.data.brandTagRequestListExcelSheet);
    } catch (error) {
      toastMsg.error(error.message || brandToastMsg.GET_BRAND_TAG_REQUEST_EXCEL_DOWNLOAD_REQUEST_ERROR);
    }
  });

  /**
   *  [브랜드] 브랜드 상세 정보 > 브랜드 정보 요청하기
   */
  yield takeEvery(brandActions.GET_BRAND_DETAIL_INFO_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(BrandApi.getBrandDetailInfo, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(brandToastMsg.GET_BRAND_DETAIL_INFO_REQUEST_SUCCESS);

        if (result.data?.info?.balanceInfo?.balancePeriodInfoList?.some((item) => isNil(item.balancePeriodType))) {
          result.data.info.balanceInfo.balancePeriodInfoList = result.data.info.balanceInfo.balancePeriodInfoList.map(
            (item) => ({
              ...item,
              balancePeriodType: BALANCE_PERIOD_TYPE.DAILY,
            }),
          );
        }

        yield put({
          type: brandActions.GET_BRAND_DETAIL_INFO_SUCCESS,
          payload: result.data,
        });
      }
    } catch (error) {
      toastMsg.error(brandToastMsg.GET_BRAND_DETAIL_INFO_REQUEST_ERROR);
    }
  });

  /**
   *  [브랜드] 브랜드 상세 정보 > 통계 데이터 요청하기
   */
  yield takeEvery(brandActions.GET_BRAND_DETAIL_STATISTIC_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(BrandApi.getBrandDetailStatistic, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(brandToastMsg.GET_BRAND_DETAIL_STATISTIC_REQUEST_SUCCESS);

        yield put({ type: brandActions.GET_BRAND_DETAIL_STATISTIC_SUCCESS, payload: result.data });
      }
    } catch (error) {
      toastMsg.error(brandToastMsg.GET_BRAND_DETAIL_STATISTIC_REQUEST_ERROR);
    }
  });

  /**
   *  [브랜드] 브랜드 상세 정보 > 정산 정보 요청하기
   */
  yield takeEvery(brandActions.GET_BRAND_DETAIL_ORDER_BALANCE_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(BrandApi.getBrandDetailOrderBalance, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(brandToastMsg.GET_BRAND_DETAIL_ORDER_BALANCE_REQUEST_SUCCESS);

        yield put({ type: brandActions.GET_BRAND_DETAIL_ORDER_BALANCE_SUCCESS, payload: result.data });
      }
    } catch (error) {
      toastMsg.error(brandToastMsg.GET_BRAND_DETAIL_ORDER_BALANCE_REQUEST_ERROR);
    }
  });

  /**
   *  [브랜드] 브랜드 상세 정보 > 상품 정보 요청하기
   */
  yield takeEvery(brandActions.GET_BRAND_DETAIL_PRODUCT_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(BrandApi.getBrandDetailProduct, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(brandToastMsg.GET_BRAND_DETAIL_PRODUCT_REQUEST_SUCCESS);
        const { productList, totalCnt } = result.data.listInfo;
        const listInfo = setResponseListForm(productList, totalCnt, payload);
        const summaryInfo = result.data.summaryInfo;
        yield put({ type: brandActions.GET_BRAND_DETAIL_PRODUCT_SUCCESS, payload: { listInfo, summaryInfo } });
      }
    } catch (error) {
      toastMsg.error(brandToastMsg.GET_BRAND_DETAIL_PRODUCT_REQUEST_ERROR);
    }
  });

  /**
   *  [브랜드] 브랜드 상세 정보 > 공지사항 정보 요청하기
   */
  yield takeEvery(brandActions.GET_BRAND_DETAIL_NOTICE_PRODUCT_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(BrandApi.getBrandDetailNoticeProduct, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(brandToastMsg.GET_BRAND_DETAIL_NOTICE_PRODUCT_REQUEST_SUCCESS);

        const data = setResponseListForm(result.data.noticeProductList, result.data.totalCnt, payload);

        yield put({ type: brandActions.GET_BRAND_DETAIL_NOTICE_PRODUCT_SUCCESS, payload: data });
      }
    } catch (error) {
      toastMsg.error(brandToastMsg.GET_BRAND_DETAIL_NOTICE_PRODUCT_REQUEST_ERROR);
    }
  });

  /**
   *  [브랜드] 브랜드 상세 정보 > 상품등록 정보 요청하기
   */
  yield takeEvery(brandActions.GET_BRAND_DETAIL_PRODUCT_REGISTER_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(BrandApi.getBrandDetailProductRegister, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(brandToastMsg.GET_BRAND_DETAIL_PRODUCT_REGISTER_REQUEST_SUCCESS);

        yield put({ type: brandActions.GET_BRAND_DETAIL_PRODUCT_REGISTER_SUCCESS, payload: result.data });
      }
    } catch (error) {
      toastMsg.error(brandToastMsg.GET_BRAND_DETAIL_PRODUCT_REGISTER_REQUEST_ERROR);
    }
  });

  /**
   *  [브랜드] 브랜드 상세 정보 > 상품 리스트 엑셀 다운로드 하기
   */
  yield takeEvery(brandActions.GET_BRAND_DETAIL_PRODUCT_EXCEL_DOWNLOAD_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(BrandApi.getBrandDetailProductExcelDownload, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(brandToastMsg.GET_BRAND_DETAIL_PRODUCT_EXCEL_DOWNLOAD_REQUEST_SUCCESS);

        excelDownLoad('브랜드 상품 정보', result.data.brandProductListExcelSheet);
      }
    } catch (error) {
      toastMsg.error(error.message || brandToastMsg.GET_BRAND_DETAIL_PRODUCT_EXCEL_DOWNLOAD_REQUEST_ERROR);
    }
  });

  /**
   *  [브랜드] 브랜드 상세 정보 > 상품 공지사항 리스트 엑셀 다운로드 하기
   */
  yield takeEvery(brandActions.GET_BRAND_DETAIL_NOTICE_EXCEL_DOWNLOAD_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(BrandApi.getBrandDetailNoticeExcelDownload, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(brandToastMsg.GET_BRAND_DETAIL_NOTICE_EXCEL_DOWNLOAD_REQUEST_SUCCESS);

        excelDownLoad('브랜드 공지사항 정보', result.data.brandProductNoticeListExcelSheet);
      }
    } catch (error) {
      toastMsg.error(brandToastMsg.GET_BRAND_DETAIL_NOTICE_EXCEL_DOWNLOAD_REQUEST_ERROR);
    }
  });

  /**
   * [브랜드] 브랜드 입점승인하기
   */
  yield takeEvery(brandActions.PERMIT_BRAND_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(BrandApi.permitBrand, setRequestForm(payload, true));

      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(brandToastMsg.PERMIT_BRAND_REQUEST_SUCCESS);

        yield put({ type: brandActions.PERMIT_BRAND_SUCCESS });

        /**
         *  이동할 페이지 url
         */
        const path = yield select((state) =>
          state.brand.listPageUrl ? state.brand.listPageUrl : BRAND_JOIN_LIST_URL_PATH,
        );

        /**
         *  검색 조건
         */
        const state = yield select((state) => {
          if (state.brand.listPageUrl === BRAND_ALL_LIST_URL_PATH) {
            return {
              filterOption: state.brand.brandList.filterOption,
              sortOption: state.brand.brandList.sortOption,
              pagination: state.brand.brandList.pagination,
            };
          } else {
            return {
              filterOption: state.brand.brandJoinList.filterOption,
              sortOption: state.brand.brandJoinList.sortOption,
              pagination: state.brand.brandJoinList.pagination,
            };
          }
        });

        reDirectPage({
          path,
          state,
        });
      }
    } catch (error) {
      toastMsg.error(brandToastMsg.PERMIT_BRAND_REQUEST_ERROR, error);
    }
  });

  /**
   * [브랜드] 브랜드 입점거절하기
   */
  yield takeEvery(brandActions.REJECT_BRAND_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(BrandApi.rejectBrand, setRequestForm(payload, true));

      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(brandToastMsg.REJECT_BRAND_REQUEST_SUCCESS);

        yield put({ type: brandActions.REJECT_BRAND_SUCCESS });

        /**
         *  이동할 페이지 url
         */
        const path = yield select((state) =>
          state.brand.listPageUrl ? state.brand.listPageUrl : BRAND_JOIN_LIST_URL_PATH,
        );

        /**
         *  검색 조건
         */
        const state = yield select((state) => {
          if (state.brand.listPageUrl === BRAND_ALL_LIST_URL_PATH) {
            return {
              filterOption: state.brand.brandList.filterOption,
              sortOption: state.brand.brandList.sortOption,
              pagination: state.brand.brandList.pagination,
            };
          } else {
            return {
              filterOption: state.brand.brandJoinList.filterOption,
              sortOption: state.brand.brandJoinList.sortOption,
              pagination: state.brand.brandJoinList.pagination,
            };
          }
        });

        reDirectPage({
          path,
          state,
        });
      }
    } catch (error) {
      toastMsg.error(brandToastMsg.REJECT_BRAND_REQUEST_ERROR, error);
    }
  });

  /**
   * [브랜드] 브랜드 정보 수정하기
   */
  yield takeEvery(brandActions.EDIT_BRAND_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(BrandApi.editBrand, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        if (result.data && result.data.isInvalidTagMsg) {
          yield asyncSimpleModal({
            type: 'error',
            title: '유효하지 않은 태그',
            message: result.data.isInvalidTagMsg,
          });
          return;
        }

        toastMsg.success(brandToastMsg.EDIT_BRAND_REQUEST_SUCCESS);

        yield put({ type: brandActions.EDIT_BRAND_SUCCESS });

        /**
         *  이동할 페이지 url
         */
        const path = yield select((state) =>
          state.brand.listPageUrl ? state.brand.listPageUrl : BRAND_ALL_LIST_URL_PATH,
        );

        /**
         *  검색 조건
         */
        const state = yield select((state) => {
          if (state.brand.listPageUrl === BRAND_JOIN_LIST_URL_PATH) {
            return {
              filterOption: state.brand.brandJoinList.filterOption,
              sortOption: state.brand.brandJoinList.sortOption,
              pagination: state.brand.brandJoinList.pagination,
            };
          } else {
            return {
              filterOption: state.brand.brandList.filterOption,
              sortOption: state.brand.brandList.sortOption,
              pagination: state.brand.brandList.pagination,
            };
          }
        });

        reDirectPage({
          path,
          state,
        });
      }
    } catch (error) {
      toastMsg.error(brandToastMsg.EDIT_BRAND_REQUEST_ERROR, error);
    }
  });

  /**
   * [브랜드] 브랜드 상품 등록 정보 수정하기
   */
  yield takeEvery(brandActions.EDIT_BRAND_PRODUCT_REGISTER_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(BrandApi.editBrandProductRegister, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(brandToastMsg.EDIT_BRAND_PRODUCT_REGISTER_REQUEST_SUCCESS);

        yield put({ type: brandActions.EDIT_BRAND_PRODUCT_REGISTER_REQUEST_SUCCESS });

        /**
         *  이동할 페이지 url
         */
        const path = yield select((state) =>
          state.brand.listPageUrl ? state.brand.listPageUrl : BRAND_ALL_LIST_URL_PATH,
        );

        /**
         *  검색 조건
         */
        const state = yield select((state) => {
          if (state.brand.listPageUrl === BRAND_JOIN_LIST_URL_PATH) {
            return {
              filterOption: state.brand.brandJoinList.filterOption,
              sortOption: state.brand.brandJoinList.sortOption,
              pagination: state.brand.brandJoinList.pagination,
            };
          } else {
            return {
              filterOption: state.brand.brandList.filterOption,
              sortOption: state.brand.brandList.sortOption,
              pagination: state.brand.brandList.pagination,
            };
          }
        });

        reDirectPage({
          path,
          state,
        });
      }
    } catch (error) {
      toastMsg.error(brandToastMsg.EDIT_BRAND_PRODUCT_REGISTER_REQUEST_ERROR, error);
    }
  });

  /**
   * [브랜드] 브랜드 티어 정보 요청하기
   */
  yield takeEvery(brandActions.GET_BRAND_TIER_INFO_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(BrandApi.getBrandTierInfo, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(brandToastMsg.GET_BRAND_TIER_INFO_REQUEST_SUCCESS);

        const data = {
          currentTierList: result.data.currentTierList,
          brandTierList: result.data.brandTierList,
          brandSelectorList: result.data.brandSelectorList,
        };

        yield put({ type: brandActions.GET_BRAND_TIER_INFO_SUCCESS, payload: data });
      }
    } catch (error) {
      toastMsg.error(brandToastMsg.GET_BRAND_TIER_INFO_REQUEST_ERROR);
    }
  });

  /**
   * [브랜드] 브랜드 티어 셀렉터 검색하기
   */
  yield takeEvery(brandActions.GET_BRAND_TIER_SELECTOR_LIST_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(BrandApi.getBrandTierSelectorList, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(brandToastMsg.GET_BRAND_TIER_SELECTOR_LIST_REQUEST_SUCCESS);

        const data = {
          brandSelectorList: result.data.brandSelectorList,
        };

        yield put({ type: brandActions.GET_BRAND_TIER_SELECTOR_LIST_SUCCESS, payload: data });
      }
    } catch (error) {
      toastMsg.error(brandToastMsg.GET_BRAND_TIER_SELECTOR_LIST_REQUEST_ERROR);
    }
  });

  /**
   * [브랜드] 브랜드 티어 수정하기
   */
  yield takeEvery(brandActions.EDIT_BRAND_TIER_LIST_REQUEST, function* ({ payload }) {
    try {
      const { newList, isUpdateRecommendList } = payload;

      /**
       *  수정된 브랜드만 보내준다.
       */
      const editedBrandTierList = [];

      forEach(newList, (tierList) => {
        forEach(tierList, (tierItem) => {
          if (tierItem.listStatus === BRAND_TIER_LIST_STATUS.MODIFIED) {
            editedBrandTierList.push(tierItem);
          } else if (tierItem.listStatus === BRAND_TIER_LIST_STATUS.ADDED) {
            editedBrandTierList.push(tierItem);
          } else if (tierItem.listStatus === BRAND_TIER_LIST_STATUS.DELETED) {
            editedBrandTierList.push(tierItem);
          }
        });
      });

      /**
       *  브랜드 티어 수정
       */
      const result = yield call(BrandApi.editBrandTierList, setRequestForm({ editedBrandTierList }, true));

      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        /**
         *  추천목록 UPDATE 를 체크한 경우에만 추천목록 수정
         */
        if (isUpdateRecommendList) {
          yield call(BrandApi.editRecommendProductList, setRequestForm({ projectType: 1 }, true));
        }

        toastMsg.success(brandToastMsg.EDIT_BRAND_TIER_LIST_REQUEST_SUCCESS);

        /**
         *  브랜드 티어 목록 갱신하기
         */
        yield put({
          type: brandActions.GET_BRAND_TIER_INFO_REQUEST,
          payload: {
            filterOption: BRAND_TIER_SELECTOR_FILTER_OPTION_MODEL,
          },
        });
      }
    } catch (error) {
      toastMsg.error(brandToastMsg.EDIT_BRAND_TIER_LIST_REQUEST_ERROR);
    }
  });

  /**
   * [브랜드] 브랜드 노출 리스트 정보 요청하기
   */
  yield takeEvery(brandActions.GET_BRAND_EXPOSURE_INFO_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(BrandApi.getBrandExposureInfo, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(brandToastMsg.GET_BRAND_EXPOSURE_INFO_REQUEST_SUCCESS);

        const data = {
          brandExposureList: result.data.brandExposureList,
          brandSelectorList: result.data.brandSelectorList,
        };

        yield put({ type: brandActions.GET_BRAND_TOP_EXPOSURE_INFO_SUCCESS, payload: data });
      }
    } catch (error) {
      toastMsg.error(brandToastMsg.GET_BRAND_EXPOSURE_INFO_REQUEST_ERROR);
    }
  });

  /**
   * [브랜드] 브랜드 노출 리스트 셀렉터 검색하기
   */
  yield takeEvery(brandActions.GET_BRAND_EXPOSURE_SELECTOR_LIST_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(BrandApi.getBrandExposureSelectorList, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(brandToastMsg.GET_BRAND_TIER_SELECTOR_LIST_REQUEST_SUCCESS);

        const data = {
          brandSelectorList: result.data.brandSelectorList,
        };

        yield put({ type: brandActions.GET_BRAND_EXPOSURE_SELECTOR_LIST_SUCCESS, payload: data });
      }
    } catch (error) {
      toastMsg.error(brandToastMsg.GET_BRAND_TIER_SELECTOR_LIST_REQUEST_ERROR);
    }
  });

  /**
   * [브랜드] 브랜드 노출 리스트 수정하기
   */
  yield takeEvery(brandActions.EDIT_BRAND_EXPOSURE_LIST_REQUEST, function* ({ payload }) {
    try {
      const { newList, tableType } = payload;

      /**
       * 수정된 브랜드만 보내준다.
       */
      const editedBrandExposureList = filter(
        newList,
        (item) =>
          item.listStatus === BRAND_EXPOSURE_LIST_STATUS.MODIFIED ||
          item.listStatus === BRAND_EXPOSURE_LIST_STATUS.ADDED ||
          item.listStatus === BRAND_EXPOSURE_LIST_STATUS.DELETED,
      );

      const result = yield call(
        BrandApi.editBrandExposureList,
        setRequestForm(
          {
            editedBrandExposureList,
            tableType,
          },
          true,
        ),
      );

      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(brandToastMsg.EDIT_BRAND_EXPOSURE_LIST_REQUEST_SUCCESS);

        /**
         * 브랜드 노출 순서 목록 갱신하기
         */
        yield put({
          type: brandActions.GET_BRAND_EXPOSURE_INFO_REQUEST,
          payload: {
            tableType,
            filterOption: BRAND_EXPOSURE_SELECTOR_FILTER_OPTION_MODEL,
          },
        });
      }
    } catch (error) {
      toastMsg.error(brandToastMsg.EDIT_BRAND_EXPOSURE_LIST_REQUEST_ERROR);
    }
  });

  /**
   *  브랜드 태그 승인하기
   */
  yield takeEvery(brandActions.PERMIT_REGISTRATION_STORE_TAG_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(BrandApi.permitRegistrationStoreTag, setRequestForm(payload));
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(brandToastMsg.PERMIT_REGISTRATION_STORE_TAG_REQUEST_SUCCESS);

        const data = {
          tagGroupType: payload.tagGroupType,
          confirmTagData: {
            [CONFIRM_TAG_LIST_TYPE.ACTIVE]: payload.storeTagList,
            [CONFIRM_TAG_LIST_TYPE.CONFIRM_READY]: [],
            [CONFIRM_TAG_LIST_TYPE.REJECT]: [],
            confirmTagButtonType: CONFIRM_TAG_LIST_TYPE.ACTIVE,
          },
        };

        yield put({ type: brandActions.PERMIT_REGISTRATION_STORE_TAG_SUCCESS, payload: data });
      }
    } catch (e) {
      toastMsg.error(brandToastMsg.PERMIT_REGISTRATION_STORE_TAG_REQUEST_ERROR, e);
    }
  });

  /**
   *  브랜드 태그 거절하기
   */
  yield takeEvery(brandActions.REJECT_REGISTRATION_STORE_TAG_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(BrandApi.rejectRegistrationStoreTag, setRequestForm(payload));
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(brandToastMsg.REJECT_REGISTRATION_STORE_TAG_REQUEST_SUCCESS);

        const data = {
          tagGroupType: payload.tagGroupType,
          rejectInfo: {
            type: payload.type,
            reason: payload.reason,
          },
        };

        yield put({ type: brandActions.REJECT_REGISTRATION_STORE_TAG_SUCCESS, payload: data });
      }
    } catch (e) {
      toastMsg.error(brandToastMsg.REJECT_REGISTRATION_STORE_TAG_REQUEST_ERROR, e);
    }
  });

  /**
   *  [브랜드] 브랜드 태그 리스트 요청하기
   */
  yield takeEvery(brandActions.GET_BRAND_TAG_COLUMN_TAG_LIST_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(BrandApi.getBrandTagColumnTagList, setRequestForm(payload));
      if (result.msg) {
        toastMsg.error(result.msg);
        return;
      }

      /**
       *  태그 보기 모달 띄우기
       */
      yield put(
        appActions.appToggleModal({
          name: 'tagListViewModal',
          data: result.data,
        }),
      );
    } catch (e) {
      toastMsg.error(brandToastMsg.GET_BRAND_TAG_COLUMN_TAG_LIST_REQUEST_ERROR, e);
    }
  });
}

export default function* brandSaga() {
  yield fork(fetchList);
}
